// Utility functions for formatting data

/**
 * Format a percentage value with consistent decimal places
 * Handles floating point precision issues
 * @param value - The percentage value (can be string or number)
 * @param decimalPlaces - Number of decimal places (default 1)
 * @returns Formatted percentage string or original value if not numeric
 */
export function formatPercentage(value: string | number | undefined | null): string {
  if (value === undefined || value === null) return '';
  
  // Convert to string first to handle both string and number inputs
  const str = String(value).trim();
  
  // Check if it looks like a percentage (ends with %)
  const hasPercent = str.endsWith('%');
  const numStr = hasPercent ? str.slice(0, -1) : str;
  
  // Try to parse as number
  const num = parseFloat(numStr);
  
  if (isNaN(num)) {
    // Not a number, return original value
    return str;
  }
  
  // Format the number
  // Use 1 decimal place for most values, but 0 for whole numbers
  let formatted: string;
  if (num === Math.floor(num)) {
    // It's a whole number
    formatted = num.toFixed(0);
  } else {
    // Has decimals - use 1 decimal place
    formatted = num.toFixed(1);
  }
  
  // Add back the % sign if it was there
  return hasPercent ? `${formatted}%` : formatted;
}

/**
 * Format source data for display on flipped cards
 * Handles various data types and applies appropriate formatting
 */
export function formatSourceDataValue(key: string, value: any): string {
  if (value === undefined || value === null) return '';
  
  // Check if the key or value suggests it's a percentage
  const lowerKey = key.toLowerCase();
  const valueStr = String(value);
  
  if (lowerKey.includes('%') || 
      lowerKey.includes('percent') || 
      lowerKey.includes('index') ||
      valueStr.includes('%')) {
    return formatPercentage(value);
  }
  
  // For numbers without %, check if they should be formatted
  const num = parseFloat(valueStr);
  if (!isNaN(num) && num !== Math.floor(num)) {
    // It's a decimal number - format to 1 decimal place
    return num.toFixed(1);
  }
  
  // Return as-is for other values
  return valueStr;
}